import React from 'react';

import cn from 'classnames';
import { Grid, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { Swiper as SwiperType } from 'swiper';
import type { SwiperProps } from 'swiper/react';

// swiper 12+ maps every `swiper/css/*` subpath to a shared `.d.ts` stub
// (with distinct runtime CSS files), which trips `import/no-duplicates`.
/* eslint-disable import/no-duplicates */
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';
/* eslint-enable import/no-duplicates */

import './style.scss';

export type CarouselSwipeDirection = 'right' | 'left';
export interface SwipeInfo {
  direction: CarouselSwipeDirection;
  activeIndex: number;
}

interface IProps {
  children: React.ReactNode[];
  rows?: number;
  className?: string;
  slidesPerView?: 'auto' | number;
  leftBleeding?: boolean;
  [other: string]: any;
  onSlide?: (swipeInfo: SwipeInfo) => void;
  showGrid?: boolean;
}

const Carousel = ({
  children,
  rows = 1,
  slidesPerView = 'auto',
  leftBleeding,
  className,
  onSlide,
  showGrid,
  ...props
}: IProps & SwiperProps) => {
  const classNames = cn('Carousel', className, {
    'swiper-buttons-hidden': true,
    'swiper-pagination-hidden': true,
    'left-bleeding': leftBleeding,
    'swiper-wrapper-grid': showGrid,
    'auto-width': slidesPerView === 'auto',
  });

  const handleOnSlideChange = React.useCallback(
    (swiper: SwiperType) => {
      if (onSlide) {
        if (swiper.activeIndex > swiper.previousIndex) {
          onSlide({ direction: 'right', activeIndex: swiper.activeIndex });
        } else {
          onSlide({ direction: 'left', activeIndex: swiper.activeIndex });
        }
      }
    },
    [onSlide],
  );

  return (
    <Swiper
      onSlideChange={handleOnSlideChange}
      modules={[Pagination, Navigation, Grid]}
      navigation
      pagination={{ clickable: true }}
      {...props}
      grid={{ rows }}
      slidesPerView={slidesPerView}
      className={classNames}
    >
      {children.map((child, index) => (
        <SwiperSlide key={index}>{child}</SwiperSlide>
      ))}
    </Swiper>
  );
};

export default Carousel;
